import React, { useState, useEffect } from "react";
import { Button, CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { faHandshakeAlt, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import type { IModalService, IPageService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import type { IPartnersRepository } from "App/Modules/Repositories";
import type { IPartner, IPartnerSlim, IResponseWithLoading } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import { getUserProfile, hasUserPermissionOnAnyPartner, jsonEqual, SearchField } from "../../Shared";

import "./_partners.scss";

type InjectedProps = {
    mmPartnersRepository: IPartnersRepository;
    irPageService: IPageService;
    mmModalService: IModalService;
};

export type PartnersProps = InjectedProps;

export const Partners = (props: PartnersProps) => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [loadingPartners, setLoadingPartners] = useState(true);
    const [partners, setPartners] = useState<IResponseWithLoading<IPartnerSlim[]>>();
    const profile = getUserProfile();
    useEffect(() => {
        let sub;
        if (!partners) {
            sub = extendWithLoadingAndFail(props.mmPartnersRepository.getPartnersIncludingGlobal()).subscribe(
                (response) => {
                    if (!response.isLoading && !jsonEqual(response, partners)) {
                        setPartners(response);
                        setLoadingPartners(false);
                    }
                },
                (error) => {
                    setPartners(error);
                    setLoadingPartners(false);
                },
            );
        }
        return () => {
            if (sub) {
                sub.unsubscribe();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { t } = useTranslation();
    const filterFunc = (partner: IPartnerSlim) => {
        if (!globalFilter) {
            return true;
        }
        return partner.name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1;
    };
    const addNewPartner = (event) => {
        event.preventDefault();
        props.mmModalService.open<IPartner, void>("irAddPartner", null).subscribe((response: IPartner) => {
            partners.data.push(response);
            window.location.hash = `#!/partners/${response.id}`;
        });
    };
    return (
        <div className="partners card">
            <h1 className="title title--h2">{t("BeforeLogin.Partner.Partners")}</h1>
            <div className="partners__functions">
                <div className="partners__filter">
                    <SearchField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} fixedWidth />}
                    onClick={addNewPartner}
                    disabled={!hasUserPermissionOnAnyPartner(profile, Permission.GlobalSysAdmin)}
                >
                    {t("Gui.Buttons.AddEntity", {
                        entityName: t("Entities.Partner.Partner").toLowerCase(),
                    })}
                </Button>
            </div>
            <div className="scrollable-list card card--nested card--alt">
                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                    <div className="entity-list">
                        {loadingPartners ? (
                            <div className="entity-list__item entity-list__item--loading">
                                <CircularProgress />
                            </div>
                        ) : null}
                        {partners?.data?.map((partner) => {
                            if (filterFunc(partner)) {
                                return (
                                    <div className="entity-list__item" key={partner.id}>
                                        <a
                                            className="entity-list__item-link entity-list__item-link--padding"
                                            href={`/#!/partners/${partner.id}`}
                                        >
                                            <div className="entity-list__item-icon entity-list__item-icon--alt">
                                                <FontAwesomeIcon icon={faHandshakeAlt} fixedWidth />
                                            </div>
                                            <div className="entity-list__item-panel">
                                                <div className="paragraph paragraph--no-spacing">{partner.name}</div>
                                            </div>
                                            <div className="entity-list__item-arrow">
                                                <FontAwesomeIcon icon={faChevronRight} />
                                            </div>
                                        </a>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
